/**
 * AdjHistoryPopover — shared by CountSheetsPage and StockCountPanel.
 *
 * Shows the adj total as a clickable badge. On 2s hover or click the popover
 * opens with the full movement timeline, an "Add entry" form, per-row delete,
 * and a single-product print button.
 *
 * Props:
 *   sheetId      — count sheet DB id
 *   productId    — product_id
 *   productName  — display name
 *   entries      — adj_entries array from DB item
 *   total        — count_adjustment sum (number)
 *   readOnly     — true if sheet is completed/cancelled
 *   onUpdate     — callback after any mutation (reload the sheet)
 */
import { useState, useRef, useEffect } from 'react';
import { api } from '../contexts/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { History, Plus, Trash2, Printer, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatTs(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function AdjHistoryPopover({
  sheetId, productId, productName, entries = [], total = 0, readOnly = false, onUpdate,
}) {
  const [open, setOpen] = useState(false);
  const [addVal, setAddVal] = useState('');
  const [addNote, setAddNote] = useState('');
  const [saving, setSaving] = useState(false);
  const hoverTimer = useRef(null);

  // 2-second hover opens
  const onMouseEnter = () => {
    hoverTimer.current = setTimeout(() => setOpen(true), 2000);
  };
  const onMouseLeave = () => {
    clearTimeout(hoverTimer.current);
  };
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const hasEntries = entries && entries.length > 0;

  const addEntry = async () => {
    const val = parseInt(addVal);
    if (isNaN(val) || val === 0) { toast.error('Enter a non-zero value'); return; }
    setSaving(true);
    try {
      await api.put(`${BACKEND_URL}/api/count-sheets/${sheetId}/items`, {
        items: [{ product_id: productId, adj_entry: { value: val, note: addNote.trim() } }],
      });
      setAddVal(''); setAddNote('');
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add entry');
    }
    setSaving(false);
  };

  const deleteEntry = async (idx) => {
    try {
      await api.delete(`${BACKEND_URL}/api/count-sheets/${sheetId}/items/${productId}/adj-entries/${idx}`);
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove entry');
    }
  };

  // Formula string: "2 + 3 - 1 = 4"
  const formula = hasEntries
    ? entries.map((e, i) => (i === 0 ? (e.value >= 0 ? `+${e.value}` : `${e.value}`) : (e.value >= 0 ? ` + ${e.value}` : ` - ${Math.abs(e.value)}`))).join('') + ` = ${total >= 0 ? '+' : ''}${total}`
    : null;

  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=480,height=600');
    const ts = new Date().toLocaleString();
    const rows = (entries || []).map(e =>
      `<tr><td style="padding:4px 8px">${e.value > 0 ? '+' : ''}${e.value}</td><td style="padding:4px 8px;color:#666">${e.note || '—'}</td><td style="padding:4px 8px;color:#999">${formatTs(e.ts)}</td></tr>`
    ).join('');
    win.document.write(`
      <html><head><title>Adj Log — ${productName}</title>
      <style>body{font-family:sans-serif;padding:24px;font-size:13px}h2{margin:0 0 4px}p{color:#666;margin:0 0 16px}table{border-collapse:collapse;width:100%}th{text-align:left;padding:4px 8px;background:#f1f5f9;font-size:11px}td{border-bottom:1px solid #e2e8f0}tfoot td{font-weight:bold;padding-top:8px}</style></head>
      <body><h2>${productName}</h2><p>Adj Movement Log · Printed ${ts}</p>
      <table><thead><tr><th>Value</th><th>Note</th><th>Time</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td colspan="2">Total adj</td><td>${total >= 0 ? '+' : ''}${total}</td></tr></tfoot>
      </table></body></html>`);
    win.document.close();
    win.print();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={() => setOpen(v => !v)}
          data-testid={`adj-history-trigger-${productId}`}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono transition-all
            ${hasEntries || total !== 0
              ? (total > 0 ? 'bg-emerald-100 text-emerald-700' : total < 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500')
              : 'text-slate-300 hover:text-slate-500'}`}
        >
          {total !== 0 ? (total > 0 ? `+${total}` : total) : '0'}
          <History size={10} className={`opacity-0 group-hover:opacity-100 transition-opacity ${hasEntries ? 'opacity-60' : ''}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent side="left" align="start" className="w-80 p-0 shadow-xl" data-testid={`adj-popover-${productId}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-lg">
          <span className="text-xs font-semibold text-slate-600 truncate max-w-[180px]">{productName}</span>
          <div className="flex items-center gap-1">
            {hasEntries && <button onClick={handlePrint} title="Print log" className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"><Printer size={13} /></button>}
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-200 text-slate-400"><X size={13} /></button>
          </div>
        </div>

        {/* Timeline */}
        <div className="px-3 py-2 max-h-52 overflow-y-auto space-y-1">
          {!hasEntries && <p className="text-xs text-slate-400 text-center py-4">No entries yet.</p>}
          {(entries || []).map((e) => (
            <div key={e.idx} className="flex items-center gap-2 group">
              <span className={`w-10 text-right font-mono text-xs font-bold shrink-0 ${e.value > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {e.value > 0 ? `+${e.value}` : e.value}
              </span>
              <span className="flex-1 text-xs text-slate-600 truncate">{e.note || <span className="text-slate-300">—</span>}</span>
              <span className="text-[10px] text-slate-300 shrink-0">{formatTs(e.ts)}</span>
              {!readOnly && (
                <button onClick={() => deleteEntry(e.idx)}
                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-all shrink-0"
                  data-testid={`adj-delete-${productId}-${e.idx}`}>
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Formula */}
        {hasEntries && (
          <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50">
            <p className="text-[11px] font-mono text-slate-500 text-right">{formula}</p>
          </div>
        )}

        {/* Add entry form */}
        {!readOnly && (
          <div className="px-3 py-2 border-t border-slate-100 space-y-1.5">
            <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Add entry</p>
            <div className="flex gap-1.5">
              <Input
                value={addVal} onChange={e => {
                  const raw = e.target.value.replace(/[^\d-]/g, '').replace(/(?!^)-/g, '');
                  setAddVal(raw);
                }}
                placeholder="±value" inputMode="text"
                className="h-7 w-16 text-center text-xs font-mono px-1"
                data-testid={`adj-add-val-${productId}`}
                onKeyDown={e => e.key === 'Enter' && addEntry()}
              />
              <Input
                value={addNote} onChange={e => setAddNote(e.target.value)}
                placeholder="note (optional)" className="h-7 text-xs flex-1 px-2"
                data-testid={`adj-add-note-${productId}`}
                onKeyDown={e => e.key === 'Enter' && addEntry()}
              />
              <Button size="sm" onClick={addEntry} disabled={saving}
                className="h-7 px-2 bg-[#1A4D2E] hover:bg-[#14532d] text-white"
                data-testid={`adj-add-submit-${productId}`}>
                <Plus size={12} />
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
